import { useCallback, useMemo, useState } from 'react';
import { pick } from 'lodash';

import Stack from '@mui/material/Stack';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { selectedOptionGroupIdsOf } from '../../utils/retained-service-usages-groups.util';
import type {
  QuotationWorkflowId,
  QuotationStateId,
  Id,
} from '@e-socna/data-layer';

import { FolderBusinessEntityState } from '../../components/FolderBusinessEntityState';
import { DocumentEditorResolvers_QuickAccess_Mission_Fragment } from '../../components/DocumentEditor/resolvers/fragments/quick-access.fragment';
import {
  getLiveServiceUsagesGroups,
  getSubMissionTitles,
} from '../../components/DocumentEditor/resolvers/utils/mission-service-usages-groups.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useFindAllDocumentTemplatesQuery } from '../../pages/DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';
import type { MissionQuotationPage_QuotationInfo } from '../../pages/MissionQuotation/fragments/quotation-info.fragment';

import type { RenderActionDialog } from '../FolderBusinessEntityState/types/action-dialog-render-props.type';

import { QuotationDocumentChoiceFields } from './DocumentChoiceFields';
import { QuotationOptionGroupsFields } from './OptionGroupsFields';
import { QuotationProofFileFields } from './ProofFileFields';
import { getQuotationOptionGroups } from './utils/quotation-option-groups.util';
import { useFindAllMissionQuotationInfoActionPermissionsQuery } from './hooks/useFindAllMissionQuotationInfoActionPermissionsQuery.hook';
import { useUpdateMissionQuotationInfoStateSubmitFactory } from './hooks/useUpdateMissionQuotationInfoStateSubmitFactory.hook';

export const MissionQuotationInfoStateForm_ResolvableMissionType_Fragment =
  graphql(`
    fragment MissionQuotationInfoStateForm_ResolvableMissionType_Fragment on ResolvableMissionType {
      quotationTemplateId
    }
  `);

interface Props {
  readonly enterEditionMode: () => void;
  readonly isInEditionMode: boolean;
  readonly leaveEditionMode: () => void;
  readonly missionId: Id;
  readonly missionQuotationInfo: MissionQuotationPage_QuotationInfo;
  readonly onSuccess?: (params: { actionId: Id; newStateId: Id }) => void;
  readonly quotationId: Id;
}

export const MissionQuotationInfoStateForm = ({
  enterEditionMode,
  isInEditionMode,
  leaveEditionMode,
  missionId,
  missionQuotationInfo,
  onSuccess,
  quotationId,
}: Readonly<Props>) => {
  const {
    genericStatusesCodeMap,
    documentTypesCodeMap,
    quotationStatesIdMap,
    quotationWorkflowsIdMap,
  } = useReferencesContext();

  const { mission, resolvableMissionType } = useMissionReadyContext();

  const { quotationState, quotationWorkflow } = ensureDefined({
    quotationWorkflow:
      quotationWorkflowsIdMap[
        missionQuotationInfo.workflowId as QuotationWorkflowId
      ],
    quotationState:
      quotationStatesIdMap[missionQuotationInfo.stateId as QuotationStateId],
  });

  const { currentNode } = ensureDefined(
    {
      currentNode: quotationWorkflow.stateMachine.find(
        (node) => node.stateId === quotationState._id,
      ),
    },
    'Current node not found',
  );

  const { data, isFetching } =
    useFindAllMissionQuotationInfoActionPermissionsQuery(
      missionId,
      isInEditionMode,
    );

  const { data: templatesData } = useFindAllDocumentTemplatesQuery();

  const quotationTemplates = useMemo(
    () =>
      (templatesData?.findAllDocumentTemplates ?? EMPTY_ARRAY).filter(
        (template) =>
          template.statusId === genericStatusesCodeMap.Enabled._id &&
          template.document.typeId === documentTypesCodeMap.Quotation._id,
      ),
    [
      documentTypesCodeMap.Quotation._id,
      genericStatusesCodeMap.Enabled._id,
      templatesData,
    ],
  );

  const quotationTemplateId = getFragmentData(
    MissionQuotationInfoStateForm_ResolvableMissionType_Fragment,
    resolvableMissionType,
  )?.quotationTemplateId;

  const suggestedTemplateIds = useMemo(
    () =>
      new Set<Id>(quotationTemplateId ? [quotationTemplateId] : EMPTY_ARRAY),
    [quotationTemplateId],
  );

  const quickAccess = getFragmentData(
    DocumentEditorResolvers_QuickAccess_Mission_Fragment,
    mission,
  );

  const optionGroups = useMemo(
    () =>
      getQuotationOptionGroups({
        liveGroups: getLiveServiceUsagesGroups(quickAccess),
        snapshotGroups:
          quickAccess.quotationInfo?.snapshot?.serviceUsagesGroups,
        subMissionTitles: getSubMissionTitles(quickAccess),
      }),
    [quickAccess],
  );

  // The client answer starts from the choice prepared in the pricing.
  const initialSelectedOptionGroupIds = quickAccess.quotationInfo?.snapshot
    ? quickAccess.quotationInfo.snapshot.selectedOptionGroupIds
    : selectedOptionGroupIdsOf(
        quickAccess.servicesInfo.consolidation.serviceUsagesGroups,
        quickAccess.servicesInfo.rejectedOptionGroupIds,
      );

  const isOptionGroupsSelectionAction = useCallback(
    (actionId: Id) =>
      !!optionGroups.length &&
      !!currentNode.actions.find((action) => action._id === actionId)?.settings
        .askOptionGroupsSelection,
    [currentNode.actions, optionGroups.length],
  );

  const useUpdateMissionQuotationInfoStateSubmit =
    useUpdateMissionQuotationInfoStateSubmitFactory({
      isOptionGroupsSelectionAction,
      missionId,
      quotationId,
      onSuccess,
    });

  const documentAlreadyExists = !!missionQuotationInfo.quotationId;

  const currentNodeActions = currentNode.actions;
  const missionTitle = mission.consolidation.title;

  const [uploadedProofFileActionId, setUploadedProofFileActionId] =
    useState<Id | null>(null);

  const handleProofFileUploadSuccess = useCallback(
    (actionId: Id) => () => {
      setUploadedProofFileActionId(actionId);
    },
    [],
  );

  const renderActionDialog: RenderActionDialog = useCallback(
    (formikBag) => {
      const selectedAction = currentNodeActions.find(
        (action) => action._id === formikBag.values.actionId,
      );

      if (!selectedAction) {
        return null;
      }

      const needsDocumentChoice =
        !documentAlreadyExists &&
        !!selectedAction.settings.autoCreateDocumentIfNotExists;
      const needsProofFile = !!selectedAction.settings.showProofFileDialog;
      const needsOptionGroupsSelection = isOptionGroupsSelectionAction(
        selectedAction._id,
      );

      if (
        !needsDocumentChoice &&
        !needsProofFile &&
        !needsOptionGroupsSelection
      ) {
        return null;
      }

      const isDocumentChoiceValid =
        // eslint-disable-next-line dot-notation -- index signature access required by noPropertyAccessFromIndexSignature
        !needsDocumentChoice || !!formikBag.values['title'];
      const isProofFileValid =
        !needsProofFile || uploadedProofFileActionId === selectedAction._id;

      return {
        title: 'Informations nécessaires pour le devis',
        isValid: isDocumentChoiceValid && isProofFileValid,
        content: (
          <Stack key={selectedAction._id} spacing={3}>
            {needsDocumentChoice ? (
              <QuotationDocumentChoiceFields
                defaultTemplateId={quotationTemplateId ?? undefined}
                defaultTitle={missionTitle}
                quotationTemplates={quotationTemplates}
                suggestedTemplateIds={suggestedTemplateIds}
              />
            ) : null}
            {needsProofFile ? (
              <QuotationProofFileFields
                defaultFileCategoryId={
                  selectedAction.settings.defaultProofFileCategoryId
                }
                missionId={missionId}
                onUploadSuccess={handleProofFileUploadSuccess(
                  selectedAction._id,
                )}
              />
            ) : null}
            {needsOptionGroupsSelection ? (
              <QuotationOptionGroupsFields
                initialSelectedOptionGroupIds={initialSelectedOptionGroupIds}
                optionGroups={optionGroups}
              />
            ) : null}
          </Stack>
        ),
      };
    },
    [
      currentNodeActions,
      documentAlreadyExists,
      handleProofFileUploadSuccess,
      isOptionGroupsSelectionAction,
      missionTitle,
      missionId,
      optionGroups,
      quotationTemplateId,
      initialSelectedOptionGroupIds,
      quotationTemplates,
      suggestedTemplateIds,
      uploadedProofFileActionId,
    ],
  );

  const currentNodeWithQuotationState = useMemo(
    () => ({
      ...currentNode,
      ...pick(quotationState, ['label', 'description', 'chip']),
    }),
    [currentNode, quotationState],
  );

  const { changeQuotationState } = useMissionAccess(
    missionId,
    missionQuotationInfo,
  );

  return (
    <FolderBusinessEntityState
      actionPermissions={data?.findOneMission.access.actions}
      areActionPermissionsLoading={isFetching}
      currentNode={currentNodeWithQuotationState}
      enableAction
      formTitle="Mise à jour du Statut du Devis de Mission"
      gate={changeQuotationState}
      isInEditionMode={isInEditionMode}
      onCancel={leaveEditionMode}
      onEdit={enterEditionMode}
      renderActionDialog={renderActionDialog}
      useUpdateStateSubmit={useUpdateMissionQuotationInfoStateSubmit}
    />
  );
};
